/**
 * Foundation 模块类型定义
 * 对应 foundation-service (7092)
 */

// ==================== 用户相关 ====================

/** 用户性别枚举 — 值必须为 number，与后端 CreateUserReq.Gender int 对齐 */
export enum UserGender {
  SECRET = 0,
  MALE = 1,
  FEMALE = 2,
}

/** 用户状态枚举 */
export enum UserStatus {
  DISABLED = 0,
  NORMAL = 1,
}

/** 用户接口 */
export interface User {
  id: string;
  tenantId?: string;
  username: string;
  realName: string;
  nickname?: string;
  avatar?: string;
  avatarUrl?: string;
  gender: UserGender;
  phone?: string;
  email?: string;
  deptId?: string;
  deptName?: string;
  orgId?: string;
  position?: string;
  positionId?: string;
  positionName?: string;
  roles?: Role[];
  status: UserStatus;
  userType?: string;
  loginTime?: string;
  lastLoginTime?: string;
  lastLoginIp?: string;
  loginCount?: number;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  intro?: string;
  remark?: string;
  birthday?: string;
  tenant?: {
    id: string;
    name: string;
    code: string;
    type: string;
  };
}

/** 用户查询参数 */
export interface UserQueryParams {
  roleId?: string;
  current?: number;
  pageSize?: number;
  username?: string;
  realName?: string;
  phone?: string;
  email?: string;
  deptId?: string;
  status?: UserStatus;
  gender?: UserGender;
  createTimeStart?: string;
  createTimeEnd?: string;
}

/** 用户表单数据 */
export interface UserFormData {
  id?: string;
  username: string;
  realName: string;
  nickname?: string;
  gender: UserGender;
  phone?: string;
  email?: string;
  orgId?: string;
  positionId?: string;
  password?: string;
  roles?: string[];
  roleIds?: number[]; // 绑定角色 id（提交经 keysToSnake 转 role_ids）
  status: UserStatus;
  intro?: string;
  birthday?: string;
}

/** 用户统计 */
export interface UserStatistics {
  totaluser: number;
  normaluser: number;
  disableduser: number;
  maleuser: number;
  femaleuser: number;
  todayLogins: number;
  weeklyLogins: number;
  monthlyLogins: number;
}

/** 用户导入响应 */
export interface UserImportResponse {
  total: number;
  success: number;
  failed: number;
  errors: ImportErrorItem[];
  results: ImportResultItem[];
}

/** 导入错误项 */
export interface ImportErrorItem {
  row: number;
  field: string;
  value: string;
  message: string;
}

/** 导入结果项 */
export interface ImportResultItem {
  row: number;
  username: string;
  name: string;
  status: string;
  message: string;
}

// ==================== 角色相关 ====================

/** 角色状态枚举 */
export enum RoleStatus {
  DISABLED = 0,
  NORMAL = 1,
}

/** 角色接口 */
export interface Role {
  id: string;
  tenantId?: string;
  code: string;
  name: string;
  type?: string;
  status: RoleStatus;
  sort?: number;
  dataScope?: string;
  note?: string;
  permissions?: string[];
  /**
   * REQ-20260521-0007: 角色可访问门户。
   * 元素取值 ["admin", "mall"]，对应 fnd_roles.portal_access JSONB 列。
   * 登录时所有用户角色的 portal_access union 形成 JWT available_portals。
   */
  portalAccess?: Array<'admin' | 'mall'>;
  /** 角色适用主体：内部用户 / 客户账号。 */
  applicableSubjects?: Array<'internal_user' | 'customer'>;
  usageScopes?: Array<'owner_tenant' | 'descendant_tenants'>;
  createdAt: string;
  updatedAt: string;
  createdUserName?: string;
}

/** 角色树节点 */
export interface RoleTreeNode {
  id: string;
  name: string;
  code: string;
  status: RoleStatus;
  type?: string;
  parentId?: string;
  sort?: number;
  userCount?: number;
  portalAccess?: Array<'admin' | 'mall'>;
  applicableSubjects?: Array<'internal_user' | 'customer'>;
  usageScopes?: Array<'owner_tenant' | 'descendant_tenants'>;
  children?: RoleTreeNode[];
}

/** 角色统计 */
export interface RoleStatistics {
  total: number;
  enabled: number;
  disabled: number;
  avgPermissions: number;
}

/** 继承的权限项（带来源角色标注） */
export interface InheritedPermission {
  id: string;
  code: string;
  name: string;
  type?: string;
  inherited: boolean;
  sourceRoleId?: string;
  sourceRoleName?: string;
}

/** 角色查询参数 */
export interface RoleQueryParams {
  current?: number;
  pageSize?: number;
  name?: string;
  code?: string;
  status?: RoleStatus;
  createTimeStart?: string;
  createTimeEnd?: string;
  /**
   * 按角色适用对象过滤（internal_user/customer），直传后端 form 参数。
   * REQ-20260615-0009：客户管理员分配角色时传 'customer'，仅显示客户适用角色。
   */
  applicable_subjects?: string;
  assignmentScene?: 'employee_assignment' | 'customer_onboarding' | 'customer_account_role_change';
}

/** 角色表单数据 */
export interface RoleFormData {
  id?: string;
  code: string;
  name: string;
  status: RoleStatus;
  note?: string;
  permissions?: string[];
  /**
   * REQ-20260521-0007: 角色可访问门户。
   * 仅 admin/mall 合法；空数组后端会兜底 ["admin"] 防失锁。
   */
  portalAccess?: Array<'admin' | 'mall'>;
  applicableSubjects?: Array<'internal_user' | 'customer'>;
  usageScopes?: Array<'owner_tenant' | 'descendant_tenants'>;
}

// ==================== 角色模板相关 ====================

/** 模板范围枚举 */
export enum TemplateScope {
  SYSTEM = 'system',
  GLOBAL = 'global',
  TENANT = 'tenant',
}

/** 角色模板接口 */
export interface RoleTemplate {
  id: number;
  name: string;
  code: string;
  description: string;
  scope: TemplateScope;
  tenantId: number;
  roleType: string;
  dataScope: string;
  isReadonly: number;
  icon: string;
  color: string;
  sort: number;
  status: number;
  version: number;
  permissionCount: number;
  menuCount: number;
  createdAt: string;
  updatedAt: string;
}

/** 角色模板详情（含权限和菜单） */
export interface RoleTemplateDetail extends RoleTemplate {
  permissions: Array<{ id: number; name: string; code: string }>;
  menus: Array<{ id: number; name: string; path: string }>;
}

/** 角色模板查询参数 */
export interface RoleTemplateQueryParams {
  page?: number;
  pageSize?: number;
  scope?: TemplateScope;
  name?: string;
  code?: string;
  status?: number;
}

/** 创建角色模板表单数据 */
export interface RoleTemplateFormData {
  name: string;
  code: string;
  description?: string;
  scope?: TemplateScope;
  roleType?: string;
  dataScope?: string;
  isReadonly?: number;
  icon?: string;
  color?: string;
  sort?: number;
  permissionIds?: number[];
  menuIds?: number[];
}

/** 更新角色模板表单数据 */
export interface RoleTemplateUpdateData {
  name?: string;
  description?: string;
  dataScope?: string;
  icon?: string;
  color?: string;
  sort?: number;
  status?: number;
}

/** 应用模板请求 */
export interface ApplyTemplateData {
  templateId: number;
}

/** 批量应用模板请求 */
export interface ApplyTemplatesData {
  templateIds: number[];
}

/** 租户初始化请求 */
export interface TenantInitData {
  templateIds?: number[];
}

/** 租户初始化响应 */
export interface TenantInitResult {
  roles: Role[];
  roleCount: number;
  initialized: boolean;
}

// ==================== 权限相关 ====================

/** 权限类型枚举 */
export enum PermissionType {
  DIRECTORY = 0,
  MENU = 1,
  BUTTON = 2,
}

/** 权限状态枚举 */
export enum PermissionStatus {
  DISABLED = 0,
  NORMAL = 1,
}

/** 权限接口 */
export interface Permission {
  id: string;
  code: string;
  title: string;
  name: string;
  menuType: PermissionType;
  parentId?: string;
  parentName?: string;
  parentKeys?: string[];
  level: number;
  sort: number;
  sortNumber?: number;
  status: PermissionStatus;
  description?: string;
  note?: string;
  icon?: string;
  path?: string;
  component?: string;
  redirect?: string;
  hidden?: boolean;
  hide?: number;
  keepAlive?: number;
  locale?: string;
  authority?: string;
  target?: number;
  tenantType?: number;
  createdAt: string;
  updatedAt: string;
  createdUserId?: number;
  updatedUserId?: number;
  children?: Permission[];
}

/** 权限查询参数 */
export interface PermissionQueryParams {
  limit?: number;
  page?: number;
  name?: string;
  code?: string;
  type?: PermissionType;
  status?: PermissionStatus;
  parentId?: string;
  createTimeStart?: string;
  createTimeEnd?: string;
}

/** 权限表单数据 */
export interface PermissionFormData {
  id?: string;
  code: string;
  name: string;
  type: PermissionType;
  parentId?: string;
  level: number;
  sort: number;
  status: PermissionStatus;
  description?: string;
  icon?: string;
  path?: string;
  component?: string;
  redirect?: string;
  hidden?: boolean;
}

// ==================== 菜单相关 ====================

/** 菜单类型枚举 — 与后端 menu_type 对齐 (1=目录, 2=菜单, 3=按钮) */
export enum MenuType {
  DIRECTORY = 1,
  MENU = 2,
  BUTTON = 3,
}

/** 菜单项接口 — 与后端 fnd_menus 表字段对齐 */
export interface MenuItem {
  id: number;
  tenant_id?: number;
  parent_id: number;
  title: string;
  name: string;
  icon?: string;
  path?: string;
  component?: string;
  redirect?: string;
  authority?: string;
  menu_type: number;
  sort_order: number;
  status: number;
  hide: number;
  keep_alive: number;
  locale?: string;
  target: number;
  work_mode?: string;
  created_at?: string;
  updated_at?: string;
  children?: MenuItem[];
}

/** 用户菜单项接口 */
export interface UserMenuItem {
  id: number;
  parent_id: number;
  title: string;
  icon: string;
  path: string;
  component: string;
  target: number;
  authority: string;
  menu_type: number;
  hide: number;
  keep_alive: number;
  locale: string;
  name: string;
  redirect: string;
  sort_order: number;
  work_mode?: string;
  children?: UserMenuItem[];
}

/** 菜单创建/更新参数 — 与后端 JSON binding 对齐 */
export interface MenuFormData {
  id?: number;
  parent_id?: number;
  name: string;
  title: string;
  menu_type: MenuType;
  path?: string;
  component?: string;
  redirect?: string;
  icon?: string;
  authority?: string;
  sort_order: number;
  hide: number;
  keep_alive: number;
  target: number;
  locale?: string;
  work_mode?: string;
}

/** 菜单树节点 */
export interface MenuTreeNode {
  key: string;
  title: string;
  value: string;
  children?: MenuTreeNode[];
}

// ==================== 部门相关 ====================

/** 部门接口 */
export interface Department {
  id: string;
  name: string;
  code?: string;
  parentId?: string;
  parentName?: string;
  level: number;
  sort: number;
  status: number;
  description?: string;
  createdAt: string;
  updatedAt: string;
  children?: Department[];
}

/** 部门查询参数 */
export interface DepartmentQueryParams {
  current?: number;
  pageSize?: number;
  name?: string;
  code?: string;
  status?: number;
  parentId?: string;
  createTimeStart?: string;
  createTimeEnd?: string;
}

/** 部门表单数据 */
export interface DepartmentFormData {
  id?: string;
  name: string;
  code?: string;
  parentId?: string;
  level: number;
  sort: number;
  status: number;
  description?: string;
}

// ==================== 组织相关 ====================

/** 组织类型枚举 */
export enum OrganizationType {
  COMPANY = 1,
  DEPARTMENT = 2,
  GROUP = 3,
  TEAM = 4,
}

/** 组织状态枚举 */
export enum OrganizationStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  DISSOLVED = 'dissolved',
}

/** 组织接口 */
export interface Organization {
  id: string;
  parent_id?: string;
  name: string;
  code: string;
  type: OrganizationType;
  level: number;
  sort: number;
  leader_id?: string;
  leader_name?: string;
  member_count: number;
  description?: string;
  address?: string;
  phone?: string;
  email?: string;
  status: 'active' | 'inactive';
  children?: Organization[];
  created_at: string;
  updated_at: string;
}

/** 组织查询参数 */
export interface OrganizationQueryParams {
  current?: number;
  pageSize?: number;
  name?: string;
  code?: string;
  type?: OrganizationType;
  status?: string;
  leader_id?: string;
}

/** 组织成员接口 */
export interface OrganizationMember {
  id: string;
  userId: string;
  userName: string;
  userCode: string;
  avatar?: string;
  email?: string;
  phone?: string;
  positionId?: string;
  positionName?: string;
  jobTitle?: string;
  joinDate: string;
  isLeader: boolean;
  isAssistant: boolean;
  status: 'active' | 'inactive' | 'transferred';
}

/** 职位接口 */
export interface Position {
  id: string;
  name: string;
  code: string;
  level: number;
  description?: string;
  responsibilities?: string[];
  requirements?: string[];
  salaryRange?: {
    min: number;
    max: number;
  };
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
}

/** 组织成员查询参数 */
export interface MemberQueryParams {
  current?: number;
  pageSize?: number;
  organizationId?: string;
  userName?: string;
  userCode?: string;
  positionId?: string;
  isLeader?: boolean;
  status?: string;
}

/** 组织创建/更新参数 */
export interface OrganizationFormData {
  id?: string;
  parentId?: string;
  name: string;
  code: string;
  type: OrganizationType;
  sort: number;
  leaderId?: string;
  assistantIds?: string[];
  description?: string;
  address?: string;
  phone?: string;
  email?: string;
  fax?: string;
  website?: string;
  establishDate?: string;
  status: OrganizationStatus;
  costCenter?: string;
  budget?: number;
}

/** 组织成员操作参数 */
export interface MemberOperationData {
  userIds: string[];
  positionId?: string;
  jobTitle?: string;
  isLeader?: boolean;
  isAssistant?: boolean;
}

/** 组织树节点 */
export interface OrganizationTreeNode {
  key: string;
  title: string;
  value: string;
  type: OrganizationType;
  memberCount: number;
  children?: OrganizationTreeNode[];
}

/** 组织统计 */
export interface OrganizationStatistics {
  totalOrganizations: number;
  totalMembers: number;
  companies: number;
  departments: number;
  groups: number;
  teams: number;
  avgMembersPerOrg: number;
  organizationGrowth: number;
  memberGrowth: number;
}

// ==================== 岗位相关 ====================

/** 岗位类别枚举 */
export enum PositionCategory {
  MANAGEMENT = 'management',
  TECHNICAL = 'technical',
  SALES = 'sales',
  OPERATIONS = 'operations',
  SUPPORT = 'support',
}

/** 岗位状态枚举 */
export enum PositionStatus {
  DISABLED = 0,
  ENABLED = 1,
}

/** 岗位接口 */
export interface PositionInfo {
  id: number;
  tenant_id: number;
  org_id?: number;
  org_name?: string;
  name: string;
  code: string;
  category: PositionCategory;
  level: number;
  description?: string;
  responsibilities?: string;
  requirements?: string;
  sort: number;
  status: PositionStatus;
  user_count?: number;
  created_at: string;
  updated_at: string;
}

/** 岗位查询参数 */
export interface PositionQueryParams {
  page?: number;
  page_size?: number;
  name?: string;
  code?: string;
  category?: PositionCategory;
  org_id?: number;
  status?: PositionStatus;
}

/** 岗位表单数据 */
export interface PositionFormData {
  id?: number;
  name: string;
  code: string;
  category: PositionCategory;
  org_id?: number;
  level?: number;
  description?: string;
  responsibilities?: string;
  requirements?: string;
  sort?: number;
  status?: PositionStatus;
}

/** 岗位类别选项 */
export interface PositionCategoryOption {
  value: string;
  label: string;
}

/** 岗位统计 */
export interface PositionStatistics {
  total: number;
  by_category: Record<string, number>;
  by_org: Record<string, number>;
  total_users: number;
}

/** 用户岗位信息 */
export interface UserPositionInfo {
  id: number;
  user_id: number;
  position_id: number;
  position_name: string;
  position_code: string;
  org_id?: number;
  org_name?: string;
  is_primary: boolean;
  start_date?: string;
  end_date?: string;
}

// ==================== 数据权限相关 ====================

/** 数据范围类型枚举 */
export enum DataScopeType {
  ALL = 'all',
  CUSTOM = 'custom',
  DEPT = 'dept',
  DEPT_AND_CHILD = 'dept_and_child',
  SELF = 'self',
}

/** 字段权限类型枚举 */
export enum FieldPermissionType {
  VISIBLE = 'visible',
  HIDDEN = 'hidden',
  MASKED = 'masked',
  READONLY = 'readonly',
}

/** 脱敏类型枚举 */
export enum MaskType {
  PHONE = 'phone',
  EMAIL = 'email',
  ID_CARD = 'id_card',
  NAME = 'name',
  BANK_NO = 'bank_no',
  ADDRESS = 'address',
  CUSTOM = 'custom',
}

/** 数据范围规则接口 */
export interface DataScopeRule {
  id: number;
  tenant_id: number;
  role_id: number;
  role_name?: string;
  resource_type: string;
  scope_type: DataScopeType;
  scope_value?: string;
  custom_sql?: string;
  priority: number;
  status: number;
  created_at: string;
  updated_at: string;
}

/** 字段权限规则接口 */
export interface FieldPermissionRule {
  id: number;
  tenant_id: number;
  role_id: number;
  role_name?: string;
  resource_type: string;
  field_name: string;
  permission: FieldPermissionType;
  mask_type?: MaskType;
  mask_pattern?: string;
  status: number;
  created_at: string;
  updated_at: string;
}

/** 数据权限查询参数 */
export interface DataPermissionQueryParams {
  page?: number;
  page_size?: number;
  role_id?: number;
  resource_type?: string;
  scope_type?: DataScopeType;
  status?: number;
}

/** 字段权限查询参数 */
export interface FieldPermissionQueryParams {
  page?: number;
  page_size?: number;
  role_id?: number;
  resource_type?: string;
  field_name?: string;
  permission?: FieldPermissionType;
  status?: number;
}

/** 数据范围表单数据 */
export interface DataScopeFormData {
  id?: number;
  role_id: number;
  resource_type: string;
  scope_type: DataScopeType;
  scope_value?: string;
  custom_sql?: string;
  priority?: number;
  status?: number;
}

/** 字段权限表单数据 */
export interface FieldPermissionFormData {
  id?: number;
  role_id: number;
  resource_type: string;
  field_name: string;
  permission: FieldPermissionType;
  mask_type?: MaskType;
  mask_pattern?: string;
  status?: number;
}

/** 资源类型选项 */
export interface ResourceTypeOption {
  value: string;
  label: string;
  fields?: Array<{
    name: string;
    label: string;
  }>;
}

/** 数据权限统计 */
export interface DataPermissionStatistics {
  total_scopes: number;
  total_field_perms: number;
  by_resource: Record<string, number>;
  by_scope_type: Record<string, number>;
}

// ==================== 登录日志相关 ====================

/** 登录方式枚举 */
export enum LoginType {
  PASSWORD = 'password',
  SMS = 'sms',
  EMAIL = 'email',
  WECHAT = 'wechat',
  DINGTALK = 'dingtalk',
  SSO = 'sso',
  QRCODE = 'qrcode',
}

/** 登录结果枚举 */
export enum LoginResult {
  SUCCESS = 'success',
  FAIL_PASSWORD = 'fail_password',
  FAIL_LOCKED = 'fail_locked',
  FAIL_DISABLED = 'fail_disabled',
  FAIL_EXPIRED = 'fail_expired',
  FAIL_CAPTCHA = 'fail_captcha',
  FAIL_NOTFOUND = 'fail_notfound',
}

/** 设备类型枚举 */
export enum DeviceType {
  PC = 'pc',
  MOBILE = 'mobile',
  TABLET = 'tablet',
  UNKNOWN = 'unknown',
}

/** 登录日志接口 */
export interface LoginLogInfo {
  id: number;
  tenant_id: number;
  user_id: number;
  username: string;
  login_type: LoginType;
  login_result: LoginResult;
  login_ip: string;
  login_location?: string;
  user_agent?: string;
  device_type?: DeviceType;
  os_name?: string;
  browser_name?: string;
  fail_reason?: string;
  session_id?: string;
  login_method?: string;
  risk_level: number;
  risk_factors?: string[];
  logout_time?: string;
  logout_type?: string;
  duration_secs?: number;
  created_at: string;
}

/** 登录日志查询参数 */
export interface LoginLogQueryParams {
  page?: number;
  page_size?: number;
  tenant_id?: number;
  user_id?: number;
  username?: string;
  login_type?: LoginType;
  login_result?: LoginResult;
  login_ip?: string;
  device_type?: DeviceType;
  risk_level?: number;
  start_time?: string;
  end_time?: string;
}

/** 登录统计结果 */
export interface LoginResultStat {
  result: string;
  count: number;
}

/** 登录类型统计 */
export interface LoginTypeStat {
  login_type: string;
  count: number;
}

/** 设备类型统计 */
export interface DeviceTypeStat {
  device_type: string;
  count: number;
}

/** 登录统计响应 */
export interface LoginStatisticsResp {
  total_logins: number;
  success_logins: number;
  failed_logins: number;
  today_users: number;
  risk_events: number;
  result_stats: LoginResultStat[];
  type_stats: LoginTypeStat[];
  device_stats: DeviceTypeStat[];
}

// ==================== 审计日志相关 ====================

/** 审计操作类型枚举 */
export enum AuditAction {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  QUERY = 'query',
  EXPORT = 'export',
  IMPORT = 'import',
  LOGIN = 'login',
  LOGOUT = 'logout',
  ENABLE = 'enable',
  DISABLE = 'disable',
  APPROVE = 'approve',
  REJECT = 'reject',
  ASSIGN = 'assign',
  UNASSIGN = 'unassign',
}

/** 审计结果枚举 */
export enum AuditResult {
  SUCCESS = 'success',
  FAILED = 'failed',
  ERROR = 'error',
}

/** 审计日志接口 */
export interface AuditLogInfo {
  id: number;
  tenant_id: number;
  user_id: number;
  username: string;
  module: string;
  resource_type: string;
  resource_id?: string;
  action: AuditAction;
  description?: string;
  method: string;
  path: string;
  status_code: number;
  result: AuditResult;
  client_ip?: string;
  duration: number;
  created_at: string;
}

/** 审计日志查询参数 */
export interface AuditLogQueryParams {
  page?: number;
  page_size?: number;
  tenant_id?: number;
  user_id?: number;
  module?: string;
  resource_type?: string;
  resource_id?: string;
  action?: AuditAction;
  result?: AuditResult;
  client_ip?: string;
  start_time?: string;
  end_time?: string;
  keyword?: string;
}

/** 模块统计 */
export interface ModuleStat {
  module: string;
  count: number;
}

/** 操作统计 */
export interface ActionStat {
  action: string;
  count: number;
}

/** 用户操作统计 */
export interface UserOperateStat {
  user_id: number;
  username: string;
  count: number;
}

/** 审计统计响应 */
export interface AuditStatisticsResp {
  total_operations: number;
  success_operations: number;
  failed_operations: number;
  module_stats: ModuleStat[];
  action_stats: ActionStat[];
  user_stats: UserOperateStat[];
}

// ==================== 权限引擎相关 ====================

/** 权限覆盖类型枚举 */
export type OverrideType = 'grant' | 'deny';

/** 权限覆盖信息 */
export interface PermissionOverrideInfo {
  id: number;
  tenantId: number;
  userId: number;
  permissionCode: string;
  overrideType: OverrideType;
  reason: string;
  grantedBy: number;
  grantedByName?: string;
  userName?: string;
  expiresAt?: string;
  status: number;
  createdAt: string;
  updatedAt: string;
}

/** 权限覆盖查询参数 */
export interface PermissionOverrideQueryParams {
  page?: number;
  pageSize?: number;
  userId?: number;
  permissionCode?: string;
  overrideType?: OverrideType;
  status?: number;
}

/** 创建权限覆盖请求 */
export interface CreatePermissionOverrideData {
  userId: number;
  permissionCode: string;
  overrideType: OverrideType;
  reason?: string;
  expiresAt?: string;
}

/** 更新权限覆盖请求 */
export interface UpdatePermissionOverrideData {
  overrideType?: OverrideType;
  reason?: string;
  expiresAt?: string;
  status?: number;
}

/** 权限检查结果 */
export interface PermissionCheckResult {
  allowed: boolean;
  source: string;
  reason?: string;
}

/** 批量权限检查请求 */
export interface BatchCheckPermissionData {
  userId: number;
  permissionCodes: string[];
}

/** 批量权限检查响应 */
export interface BatchCheckPermissionResult {
  results: Record<string, PermissionCheckResult>;
}

/** 权限溯源请求 */
export interface TracePermissionData {
  userId: number;
  permissionCode: string;
}

/** 溯源层结果 */
export interface TraceLayerResult {
  layer: string;
  checked: boolean;
  result: string;
  detail?: string;
}

/** 权限溯源响应 */
export interface TracePermissionResult {
  userId: number;
  tenantId: number;
  permissionCode: string;
  finalResult: PermissionCheckResult;
  layers: TraceLayerResult[];
}

/** 权限审计日志信息 */
export interface PermissionAuditLogInfo {
  id: number;
  tenantId: number;
  userId: number;
  userName?: string;
  action: string;
  permissionCode: string;
  resource: string;
  method: string;
  result: string;
  source: string;
  operatorId?: number;
  operatorName?: string;
  details?: string;
  ip?: string;
  createdAt: string;
}

/** 权限审计日志查询参数 */
export interface PermissionAuditLogQueryParams {
  page?: number;
  pageSize?: number;
  userId?: number;
  action?: string;
  permissionCode?: string;
  resource?: string;
  result?: string;
  source?: string;
  startTime?: string;
  endTime?: string;
}

// ==================== 用户变更日志相关 ====================

/** 变更日志操作类型 */
export type ChangeLogAction = 'create' | 'update' | 'delete' | 'enable' | 'disable'
  | 'reset_password' | 'assign_role' | 'remove_role' | 'transfer' | 'assign_position'
  | 'remove_position' | 'add_org' | 'remove_org' | 'set_primary_org';

/** 用户变更日志 */
export interface UserChangeLog {
  id: number;
  user_id: number;
  operator_id: number;
  operator_name: string;
  action: ChangeLogAction;
  module: string;
  description: string;
  old_value?: string;
  new_value?: string;
  ip?: string;
  created_at: string;
}

/** 用户变更日志查询参数 */
export interface UserChangeLogParams {
  page?: number;
  page_size?: number;
  action?: ChangeLogAction;
  start_time?: string;
  end_time?: string;
}

/** 用户调岗请求数据 */
export interface TransferUserData {
  target_org_id: string;
  target_position_id?: string;
  reason?: string;
}

/** 组织维度角色 */
export interface UserRoleWithOrg {
  role_id: string;
  role_name: string;
  role_code: string;
  org_id: string;
  org_name: string;
  assigned_at: string;
}

/** 组织维度角色分配请求 */
export interface AssignRoleWithOrgData {
  role_ids: number[];
  org_id: number;
}

/** 用户组织信息 */
export interface UserOrganizationInfo {
  id: string;
  org_id: string;
  org_name: string;
  org_code: string;
  org_type: OrganizationType;
  position_name?: string;
  is_primary: boolean;
  joined_at: string;
}

/** 批量添加用户到组织请求 */
export interface BatchAddToOrgData {
  user_ids: string[];
  org_id: string;
  position_id?: string;
}

// ==================== 消息中心相关 ====================

/** 消息类型枚举 */
export type MessageType = 'system' | 'notice' | 'todo' | 'alert';

/** 公告类型枚举 */
export type AnnouncementType = 'notice' | 'activity' | 'update';

/** 公告级别枚举 */
export type AnnouncementLevel = 'normal' | 'important' | 'urgent';

/** 消息信息 */
export interface MessageInfo {
  id: number;
  tenantId: number;
  userId: number;
  title: string;
  content: string;
  type: MessageType;
  scene: string;
  level: AnnouncementLevel;
  displayMode: 'center' | 'banner' | 'modal';
  popupPolicy: 'none' | 'once' | 'daily' | 'until_read';
  actionLabel?: string;
  actionUrl?: string;
  payload?: Record<string, unknown>;
  isRead: number; // 0未读 1已读
  readAt?: string;
  createdAt: string;
}

/** 消息查询参数 */
export interface MessageQueryParams {
  page?: number;
  pageSize?: number;
  type?: MessageType;
  isRead?: number; // 0未读 1已读
}

/** 标记已读请求 */
export interface MessageMarkReadReq {
  ids: number[]; // 为空则全部标记已读
}

/** 删除消息请求 */
export interface MessageDeleteReq {
  ids: number[];
}

/** 未读消息数响应 */
export interface MessageUnreadCountResp {
  /** 消息 + 公告合计（铃铛） */
  count: number;
  messageCount: number;
  announcementCount: number;
}

export interface MessageBatchOperationResp {
  updatedCount: number;
}

/** 消息详情响应 */
export interface MessageDetailResp {
  id: number;
  title: string;
  content: string;
  type: MessageType;
  isRead: number;
  readAt?: string;
  createdAt: string;
}

/** 公告信息 */
export interface AnnouncementInfo {
  id: number;
  tenantId: number;
  title: string;
  content: string;
  type: AnnouncementType;
  level: AnnouncementLevel;
  status: number; // 0草稿 1已发布 2已下架
  publishAt?: string;
  expireAt?: string;
  viewCount: number;
  topFlag: number; // 0不置顶 1置顶
  createdBy: number;
  creatorName?: string;
  createdAt: string;
  updatedAt: string;
  audienceMode: 'self' | 'direct_children' | 'all_descendants' | 'selected' | 'channel';
  recipientMode: 'admins' | 'all_users';
  includeSelf: boolean;
  /** 按渠道圈选时选中的渠道 */
  audienceChannelIds?: number[];
  /** 按渠道圈选时是否连带命中客户的全部下游 */
  includeDescendants?: boolean;
  recipientPolicy: 'snapshot' | 'dynamic_until_expiry';
  displayMode: 'center' | 'banner' | 'modal';
  popupPolicy: 'none' | 'once' | 'daily' | 'until_read' | 'until_ack' | 'daily_first';
  requireAck: boolean;
  actionLabel?: string;
  actionUrl?: string;
  channels: Array<'site' | 'email' | 'sms' | 'wechat'>;
  deliveryStatus: 'pending' | 'processing' | 'completed' | 'partial_failed' | 'failed';
  deliveryError?: string;
  recipientCount: number;
  deliveredCount: number;
  failedCount: number;
  version: number;
  supersedesAnnouncementId?: number;
  snapshotAt?: string;
  targetTenantIds?: number[];
  isRead?: boolean;
  readAt?: string;
  acknowledgedAt?: string;
}

/** 公告查询参数 */
export interface AnnouncementQueryParams {
  page?: number;
  pageSize?: number;
  type?: AnnouncementType;
  level?: AnnouncementLevel;
  status?: number;
  keyword?: string;
}

/** 公告详情响应 */
export interface AnnouncementDetailResp {
  id: number;
  title: string;
  content: string;
  type: AnnouncementType;
  level: AnnouncementLevel;
  topFlag: number;
  viewCount: number;
  publishAt?: string;
  expireAt?: string;
  createdAt: string;
  displayMode: 'center' | 'banner' | 'modal';
  popupPolicy: 'none' | 'once' | 'daily' | 'until_read' | 'until_ack' | 'daily_first';
  requireAck: boolean;
  actionLabel?: string;
  actionUrl?: string;
  isRead: boolean;
  readAt?: string;
  acknowledgedAt?: string;
}

/** 创建公告请求（管理端） */
export interface CreateAnnouncementReq {
  title: string;
  content: string;
  type?: AnnouncementType;   // 默认 notice
  level?: AnnouncementLevel; // 默认 normal
  publishAt?: string;
  expireAt?: string;
  topFlag?: number;          // 0不置顶 1置顶
  audienceMode?: AnnouncementInfo['audienceMode'];
  recipientMode?: AnnouncementInfo['recipientMode'];
  includeSelf?: boolean;
  audienceChannelIds?: number[];
  includeDescendants?: boolean;
  recipientPolicy?: AnnouncementInfo['recipientPolicy'];
  displayMode?: AnnouncementInfo['displayMode'];
  popupPolicy?: AnnouncementInfo['popupPolicy'];
  requireAck?: boolean;
  actionLabel?: string;
  actionUrl?: string;
  channels?: AnnouncementInfo['channels'];
  supersedesAnnouncementId?: number;
  targetTenantIds?: number[];
}

/** 更新公告请求（管理端） */
export interface UpdateAnnouncementReq {
  title?: string;
  content?: string;
  type?: AnnouncementType;
  level?: AnnouncementLevel;
  publishAt?: string;
  expireAt?: string;
  topFlag?: number;
  audienceMode?: AnnouncementInfo['audienceMode'];
  recipientMode?: AnnouncementInfo['recipientMode'];
  includeSelf?: boolean;
  audienceChannelIds?: number[];
  includeDescendants?: boolean;
  recipientPolicy?: AnnouncementInfo['recipientPolicy'];
  displayMode?: AnnouncementInfo['displayMode'];
  popupPolicy?: AnnouncementInfo['popupPolicy'];
  requireAck?: boolean;
  actionLabel?: string;
  actionUrl?: string;
  channels?: AnnouncementInfo['channels'];
  supersedesAnnouncementId?: number;
  targetTenantIds?: number[];
}

export interface AnnouncementAudiencePreviewResp {
  tenants: Array<{ tenantId: number; depth: number; directChildTenantId: number; recipientCount: number }>;
  tenantCount: number;
  recipientCount: number;
  /** 范围内被停用、收不到的账号数 */
  disabledUserCount: number;
  /** 被冻结/注销/过期而排除的租户数（含渠道命中但不在客户树内的） */
  inactiveTenantCount: number;
}

/** 按渠道命中的客户租户（ERP） */
export interface ChannelCustomerTenantsResp {
  tenantIds: number[];
  matchedCustomerCount: number;
  skippedCustomerCount: number;
}

export interface AnnouncementStatisticsResp {
  recipientCount: number;
  /** 发布时快照投递人数 */
  initialCount: number;
  /** 发布后补发给新账号的人数 */
  backfilledCount: number;
  deliveredCount: number;
  failedCount: number;
  deliveryStatus: AnnouncementInfo['deliveryStatus'];
  deliveryError?: string;
  readCount: number;
  acknowledgedCount: number;
  unreadCount: number;
  groups: Array<{ directChildTenantId: number; recipientCount: number; readCount: number; acknowledgedCount: number; unreadCount: number }>;
  channels: Array<{ channel: 'site' | 'email' | 'sms' | 'wechat'; pending: number; sent: number; skipped: number; failed: number }>;
}

/** 发送消息请求（管理端） */
export interface SendMessageReq {
  user_ids: number[];
  title: string;
  content: string;
  type?: MessageType; // 默认 system
}

/** 发送消息响应 */
export interface SendMessageResp {
  sent_count: number;
}

/** 批量标记已读请求 */
export interface BatchReadMessagesReq {
  message_ids: number[];
}

// ==================== 通用类型 ====================

/** 上传图片响应 */
export interface UploadImageResponse {
  url: string;
  path: string;
}
